import {
  channelRoute,
  foldHistoryPages,
  formatRoute,
  historyNotes,
  nextHistoryPage,
  videoBadgeOf,
  type CachedVideoMeta,
  type HistoryListing,
  type HistoryNote,
  type HistoryPage,
  type HistoryRow,
} from '@visious/core';
import { describeAuthProblem, type ProblemCopy, type ProblemSubject } from '../../api/problem';
import type { AuthApiProblem } from '../../api/auth';
import {
  matchesGridSearch,
  progressMarker,
  toListingProgress,
  type ListingBadge,
  type ListingMarker,
  type ListingProgress,
  type ProgressLookup,
} from '../../listing/model';
import type { QueueCandidate } from '../../queue/model';
import { formatDuration } from '../../format';
import { fmt, text } from '../../strings';

/**
 * §8's History page, as reasoning. R13: no DOM, no React — the queries, the cache and the
 * visibility observer are in `useHistory.ts`, and `view.tsx` renders props.
 *
 * **The page is one hydration problem wearing a list's clothes.** The endpoint returns bare video ids, so
 * what arrives is complete as an *ordering* and empty as *content*: every row's thumbnail, link and
 * position are known immediately (the thumbnail is derivable from the id — `videoFramePath`), and every
 * row's title, author, duration and date costs an 89–262 KB request. So a row has two legitimate render
 * states and this module names both rather than treating the first as loading.
 *
 * ## Why `toListingItems` is not used here, and that is the interesting decision
 *
 * Every other list in this app goes through 2b's shared listing layer. This one cannot, because of
 * `feedScrape`: that rule reads a *response* where no row has a duration, a view count or a date as a
 * degraded scrape, and blanks those fields so a page cannot print "0:00 · 0 views · now". It is the right
 * rule for a feed. Here it would be exactly wrong — on first paint **no** row has any of the three, not
 * because the instance failed to scrape them but because this page has deliberately not asked yet. Passing
 * a half-hydrated list through the shared layer would grade the reader's own laziness as an upstream
 * failure and then blank the rows that *did* arrive.
 *
 * So rows are formatted per row, from the authoritative record: a hydrated row's fields come straight from
 * `/api/v1/videos/{id}`, where `published` is genuine epoch seconds and the duration is the video's own.
 * There is no partial-truth case to defend against, which is what makes the per-row reading safe here and
 * unsafe there.
 */

export interface HistoryRowView {
  /** React's key and the removal key. Distinct by construction — the fold dedupes. */
  readonly videoId: string;
  readonly href: string;
  readonly thumbnailUrl: string | null;
  readonly badge: ListingBadge | null;
  /**
   * The cached length, or `null` while the row is still an id. Only the hover preview reads it, as
   * the clamp against the storyboard's over-emitted blank tail (`useGridPreview`) — the visible
   * duration is the badge's business.
   */
  readonly durationSec: number | null;
  /**
   * `null` until the row is hydrated. Not an empty string: the view draws a placeholder for an absent
   * title and would draw an empty line for an empty one.
   */
  readonly title: string | null;
  readonly author: string | null;
  readonly authorHref: string | null;
  readonly authorVerified: boolean;
  readonly viewCountText: string | null;
  readonly viewCountTitle: string | null;
  readonly publishedText: string | null;
  readonly publishedTitle: string | null;
  /** "Unavailable" for a row whose video is gone, or 4b's `Resume` — see {@link UNAVAILABLE_MARKER}. */
  readonly marker: ListingMarker | null;
  /**
   * The row's video is gone. **Its own field since 4b**, and it had to become one: the view styled a dead
   * row by testing `marker !== null`, which was true exactly when the marker was `UNAVAILABLE_MARKER` —
   * until this slice gave a perfectly live row a marker of its own and every resumable video would have
   * been greyed out as deleted.
   */
  readonly unavailable: boolean;
  /** 4b's bar. Drawn on every row here, which is more than a feed gets — see {@link toRowView}. */
  readonly progress: ListingProgress | null;
  /** True while its own removal is in flight. Per row, so removing one does not grey out the rest. */
  readonly removing: boolean;
}

/**
 * A row as the queue's two controls see it (`queue/model.ts`).
 *
 * The one conversion this page needs is the **title**: a history row is a bare video id until its
 * metadata arrives, and a queue entry has to carry a name. The id stands in, which is exactly what
 * the card itself draws in the meantime, so a video queued before its lookup lands reads the same in
 * both places rather than as a blank row.
 */
export function historyQueueCandidate(row: HistoryRowView, title: string | null): QueueCandidate {
  return {
    videoId: row.videoId,
    href: row.href,
    title: title ?? row.videoId,
    author: row.author,
    authorHref: row.authorHref,
    thumbnailUrl: row.thumbnailUrl,
    durationSec: row.durationSec,
  };
}

export type HistoryScreenState =
  | { readonly kind: 'loading'; readonly skeletonRows: number }
  | { readonly kind: 'problem'; readonly copy: ProblemCopy }
  | { readonly kind: 'empty'; readonly title: string; readonly detail: string }
  | {
      readonly kind: 'ready';
      readonly rows: readonly HistoryRowView[];
      /** The grid search emptied the page — a sentence, or an emptied grid reads as no history. */
      readonly nothingMatches: boolean;
      readonly notices: readonly HistoryNotice[];
      /** `null` when the last page proved it was the last — see `nextHistoryPage`. */
      readonly nextPage: number | null;
      readonly listing: HistoryListing;
    };

export interface HistoryNotice {
  readonly id: HistoryNote['kind'];
  readonly text: string;
  readonly tone: 'info' | 'caution';
}

/**
 * R7's other side: core decides which notes are true (`historyNotes`), the words are ours.
 *
 * Only `malformed` still earns a sentence, and it earns it by being the one note the grid cannot show
 * on its own: a row that could not be read is a row the page never draws, so without this the reader
 * sees a shorter history and nothing saying why.
 *
 * The other three are all things the page already tells them better. `unavailable` counted the dead
 * videos, and each of those rows carries {@link unavailableMarker} in place, next to the title it
 * belongs to — the banner was a tally of what was on screen (dropped 2026-08-07). `shifted` and
 * `dropped` were sentences about the *pages*: rows already further up and not repeated, rows that could
 * not be read at all. Core still counts every one of them, and the counts are worth having; none of
 * them is the reader's history, and none is anything they can act on.
 */
function noticeFor(note: HistoryNote): HistoryNotice | null {
  const words = text().history.notice;
  switch (note.kind) {
    case 'malformed':
      return { id: 'malformed', tone: 'caution', text: words.malformed };
    case 'unavailable':
    case 'shifted':
    case 'dropped':
      return null;
  }
}

/**
 * The marker this page draws for a dead row. 4b adds one more — and *only* one, which is the note that
 * used to be here brought up to date.
 *
 * The reasoning it left behind still holds where it was right: "Watched" on a history row is noise,
 * because every row on this page is watched by construction. What 4b changed is that a **partly**
 * watched row is not — `Resume` distinguishes it from the rows next to it and says what clicking does.
 * So a completed row gets the bar and no word, and a partial one gets both (`toRowView`).
 *
 * The other half of that old note is answered too: putting a marker on *feed* rows needed the watched
 * set on pages that work signed out, and it turned out not to need the account at all. The positions
 * 4b stores locally are a stronger signal than the history's id list — they carry how far — and they
 * cost no request, so Popular, Search and the channel tabs draw bars while signed out.
 */
function unavailableMarker(): ListingMarker {
  return text().history.unavailableMarker;
}

export interface HistoryStateInput {
  readonly pages: readonly HistoryPage[] | null;
  readonly problem: AuthApiProblem | null;
  /** The hydration fold — cache in, rows out (`history/hydrate.ts`). `null` before the list arrives. */
  readonly rows: readonly HistoryRow[] | null;
  readonly unavailable: number;
  readonly removing: ReadonlySet<string>;
  readonly resolveAsset: (path: string) => string | null;
  readonly pageSize: number;
  readonly nowEpochSec: number;
  /** 4b's stored playheads (`useWatchProgressLookup`) — the "where did I stop" this page is for. */
  readonly progress: ProgressLookup;
  /**
   * The grid search — display-only, and honest only about what has hydrated: this endpoint
   * serves bare ids, so a row whose title has not been looked up yet cannot match and is hidden
   * with the rest. The trade is accepted rather than accidental — the alternative is a search box
   * that triggers the 89–262 KB lookup for every row on the list.
   */
  readonly search: string;
}

export function historyState(input: HistoryStateInput): HistoryScreenState {
  if (input.problem !== null) {
    return { kind: 'problem', copy: describeAuthProblem(input.problem, historyProblemSubject()) };
  }
  if (input.pages === null || input.rows === null) return { kind: 'loading', skeletonRows: 8 };

  const listing = foldHistoryPages(input.pages);
  const notices = historyNotes(listing, input.unavailable).flatMap((note) => noticeFor(note) ?? []);

  if (listing.ids.length === 0) {
    if (listing.malformed) return { kind: 'problem', copy: text().history.malformedList };
    /*
     * The one genuinely ambiguous response in this endpoint: `200 []` is "no history", "past the last
     * page" and (before the clamp) "max_results=0", byte-identically. Only the first can reach here —
     * the pager never asks past a short page and the clamp makes the third unreachable — so this is a
     * real empty state rather than a guess, and it is worth saying that recording is on.
     */
    return { kind: 'empty', ...text().history.empty };
  }

  const all = input.rows.map((row) => toRowView(row, input));
  const rows = all.filter((row) => matchesGridSearch(input.search, row.title, row.author));

  return {
    kind: 'ready',
    rows,
    nothingMatches: all.length > 0 && rows.length === 0,
    notices,
    nextPage: nextHistoryPage(input.pages, input.pageSize),
    listing,
  };
}

function toRowView(row: HistoryRow, input: HistoryStateInput): HistoryRowView {
  const href = formatRoute({
    kind: 'watch',
    videoId: row.videoId,
    // A history click is a fresh watch: it inherits neither a timestamp (the API stores none — there is
    // no resume position anywhere in it) nor a playlist.
    startSec: null,
    playlistId: null,
    index: null,
  });
  const watched = input.progress(row.videoId);
  const base = {
    videoId: row.videoId,
    href,
    // Derived from the id against the *current* instance, so it survives an instance switch (R14).
    thumbnailUrl: input.resolveAsset(row.thumbnailPath),
    removing: input.removing.has(row.videoId),
    progress: toListingProgress(watched.mark),
    unavailable: row.unavailable,
    durationSec: null,
  };

  if (row.unavailable) {
    return {
      ...base,
      // No image: the frame for a dead id never answers at all (it hangs rather than 404s), so a URL
      // here would leave a permanently pending request behind every such row.
      thumbnailUrl: null,
      badge: null,
      title: null,
      author: null,
      authorHref: null,
      authorVerified: false,
      viewCountText: null,
      viewCountTitle: null,
      publishedText: null,
      publishedTitle: null,
      marker: unavailableMarker(),
      // No bar either: a row with nothing behind it has no length to be part-way through, and the
      // reader is being told to remove it rather than to carry on watching it.
      progress: null,
    };
  }

  const meta: CachedVideoMeta | null = row.meta;
  if (meta === null) {
    return {
      ...base,
      badge: null,
      title: null,
      author: null,
      authorHref: null,
      authorVerified: false,
      viewCountText: null,
      viewCountTitle: null,
      publishedText: null,
      publishedTitle: null,
      marker: partialMarker(watched),
    };
  }

  return {
    ...base,
    badge: toBadge(meta.lengthSeconds),
    // `0` means "not reported" on this endpoint the way it does everywhere else, and the clamp
    // treats a non-positive length as no clamp at all — so it goes through as an absence.
    durationSec: meta.lengthSeconds > 0 ? meta.lengthSeconds : null,
    title: meta.title,
    author: meta.author === '' ? null : meta.author,
    // From `authorId` alone, which the cache stores as `null` for a collaboration video — so a byline
    // with no single channel behind it is unlinked rather than pointing at "Nothing here".
    authorHref: meta.authorId === null ? null : formatRoute(channelRoute(meta.authorId)),
    authorVerified: meta.authorVerified,
    viewCountText: fmt().count(meta.viewCount),
    viewCountTitle: fmt().distinctExactTitle(fmt().count(meta.viewCount), fmt().exactCount(meta.viewCount)),
    publishedText: fmt().published(meta.publishedEpochSec, input.nowEpochSec),
    // Honest here, unlike search and the rail: `/api/v1/videos/{id}`'s top-level `published` is real
    // epoch seconds rather than a value back-computed from "2 years ago".
    publishedTitle: fmt().absoluteDate(meta.publishedEpochSec),
    marker: partialMarker(watched),
  };
}

/**
 * `Resume` for a row still in progress, nothing for a finished one — see {@link unavailableMarker}.
 *
 * The "in the account's history" half of the shared helper is deliberately not consulted: every row
 * on this page is in it, so a marker built from that fact would put "Watched" on all of them, which
 * is the noise the note above is about.
 */
function partialMarker(watched: ReturnType<ProgressLookup>): ListingMarker | null {
  const { mark } = watched;
  return mark === null || mark.complete ? null : progressMarker(watched);
}

/**
 * The duration badge, through core's own decision so the "never render 0:00" rule stays in one place.
 * A history row can only ever be a duration or nothing: `liveNow` and `isUpcoming` are not cached,
 * because a video you have already watched is neither.
 */
function toBadge(lengthSeconds: number): ListingBadge | null {
  const badge = videoBadgeOf({
    liveNow: false,
    isUpcoming: false,
    lengthSeconds,
    premiereAtEpochSec: null,
  });
  return badge.kind === 'duration'
    ? { kind: 'duration', text: formatDuration(badge.seconds), title: null }
    : null;
}

/* ------------------------------------------------------------------------------------------------
 * Copy
 * ---------------------------------------------------------------------------------------------- */

function historyProblemSubject(): ProblemSubject {
  const words = text().history.problem;
  return {
    notFound: words.notFound,
    unreadable: () => words.unreadable,
    disabled: words.disabled,
  };
}

/**
 * The copy for the two writes. Neither goes through `describeWriteProblem` — that one's `client` arm says
 * "the request body was wrong", which is right for the playlist endpoints and wrong here: these two
 * writes have no body at all. A `400` from them means the video id was refused, which this build makes
 * unreachable by validating the id before building a path.
 */
export function describeHistoryWriteProblem(
  problem: AuthApiProblem | { readonly kind: 'bad-video-id'; readonly videoId: string },
  action: 'remove' | 'clear',
): ProblemCopy {
  const words = text().history.write;
  if (problem.kind === 'bad-video-id') {
    return {
      title: words.badVideoIdTitle,
      detail: words.badVideoId,
      retryable: false,
    };
  }
  if (problem.kind === 'transport' && problem.error.kind === 'client') {
    return {
      title: action === 'remove' ? words.refusedRemove : words.refusedClear,
      detail: words.refusedDetail,
      retryable: false,
    };
  }
  return describeAuthProblem(problem, historyProblemSubject());
}
